import { outflowManyOffers } from 'reactApp/appHelpers/featuresHelpers/features/outflowManyOffers';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import type { OutflowOfferWebProps } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers.types';
import { cancelAutoRenewOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/cancelAutoRenew.offer';
import { captchaAndSurvey } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/captchaAndSurvey';
import { checkDiscountOffer, discountOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/discount.offer';
import { checkFamilyOffer, familyOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/family.offer';
import { checkPauseOffer, pauseOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/pause.offer';
import {
    checkUnlimitedAutoUploadOffer,
    unlimitedAutoUploadOffer,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/unlimitedAutoUpload.offer';
import { checkVkMusicOffer, vkMusicOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/vkMusic.offer';
import { createOfferProcessor } from 'reactApp/modules/subscriptionRenewal/outflowOffers/subscriptionRenewal.offers';
import { checkSubscriptionRenewedTimes } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.helpers';
import { isVkMusicSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import type { Product } from 'reactApp/types/Billing';

import { OutflowOffers } from './offers.types';

/**
 * @description Регистрация порядка показа предложений при отписке для веб версии
 *
 * @param subscription - подписка
 * @param product - продукт
 * @returns массив указывающий какие оферы и в каком порядке будут показаны
 */
export function getWebOutflowOffers(subscription: BillingDataSubscriptionInfo, product: Product) {
    const { outflowOffersMinHighPayments, outflowOffersMinHighSpace, enabled: manyOffersExpEnabled } = outflowManyOffers;
    if (isVkMusicSubscription(subscription)) {
        return [OutflowOffers.ARE_YOU_SURE, OutflowOffers.VK_MUSIC, OutflowOffers.CAPTCHA_WITH_SURVEY];
    }
    // tempexp_19437-start
    if (manyOffersExpEnabled) {
        const frequentlyRenewed = checkSubscriptionRenewedTimes(subscription, product, outflowOffersMinHighPayments);
        const largeTariff = product.space.original >= outflowOffersMinHighSpace ? OutflowOffers.UNLIMITED_AUTOUPLOAD : undefined;
        return [
            OutflowOffers.ARE_YOU_SURE,
            largeTariff ? OutflowOffers.UNLIMITED_AUTOUPLOAD : undefined,
            OutflowOffers.FAMILY,
            OutflowOffers.PAUSE,
            frequentlyRenewed ? OutflowOffers.DISCOUNT : undefined,
            OutflowOffers.CAPTCHA_WITH_SURVEY,
        ].filter(Boolean) as OutflowOffers[];
    }
    // tempexp_19437-end

    return [
        OutflowOffers.UNLIMITED_AUTOUPLOAD,
        OutflowOffers.ARE_YOU_SURE,
        OutflowOffers.FAMILY,
        OutflowOffers.PAUSE,
        OutflowOffers.DISCOUNT,
        OutflowOffers.CAPTCHA_WITH_SURVEY,
    ];
}

/**
 * Регистрация предложений для показа
 * @param offer - функция показывающая оффер
 * @param check - функция определяющая доступность показа оффера
 */
export const { processOuflowOfferList: processWebOuflowOfferList, processOutflowOffer: processWebOutflowOffer } =
    createOfferProcessor<OutflowOfferWebProps>({
        [OutflowOffers.VK_MUSIC]: {
            check: checkVkMusicOffer,
            offer: vkMusicOffer,
        },
        [OutflowOffers.ARE_YOU_SURE]: {
            offer: cancelAutoRenewOffer,
        },
        [OutflowOffers.FAMILY]: {
            check: checkFamilyOffer,
            offer: familyOffer,
        },
        [OutflowOffers.UNLIMITED_AUTOUPLOAD]: {
            check: checkUnlimitedAutoUploadOffer,
            offer: unlimitedAutoUploadOffer,
        },
        [OutflowOffers.DISCOUNT]: {
            check: checkDiscountOffer,
            offer: discountOffer,
        },
        [OutflowOffers.PAUSE]: {
            check: checkPauseOffer,
            offer: pauseOffer,
        },
        [OutflowOffers.CAPTCHA_WITH_SURVEY]: {
            offer: captchaAndSurvey,
        },
    });
